import { useAtomValue, useSetAtom } from "jotai";
import { Cylinder, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import ConfirmModal from "@/components/ConfirmModal";
import Button from "@/components/ui/Button";
import { FAB } from "@/components/ui/FAB";
import { apiService } from "@/api/service";
import type { AUTProjectTank } from "@/api/queries/account";
import { devicesAtom, projectTanksByProjectIdAtom, refetchDataAtom } from "@/store";
import { updateProjectTankAtom } from "@/store/crud";
import EditTankControllerDialog from "./EditTankControllerDialog";

interface ProjectTanksPanelProps {
  projectId: string;
  onAssign: () => void;
}

function ProjectTanksPanel({ projectId, onAssign }: ProjectTanksPanelProps) {
  const getProjectTanks = useAtomValue(projectTanksByProjectIdAtom);
  const allDevices = useAtomValue(devicesAtom);
  const updateProjectTank = useSetAtom(updateProjectTankAtom);
  const refetchData = useSetAtom(refetchDataAtom);

  const [unassignTarget, setUnassignTarget] = useState<AUTProjectTank | null>(null);
  const [stepTankCount, setStepTankCount] = useState<number>(0);
  const [unassigning, setUnassigning] = useState(false);
  const [editTarget, setEditTarget] = useState<AUTProjectTank | null>(null);

  const projectTanks = useMemo(() => getProjectTanks(projectId), [getProjectTanks, projectId]);

  const activeProjectTanks = useMemo(() => projectTanks.filter((pt) => !pt.end_date), [projectTanks]);

  const deviceById = useMemo(() => {
    const map = new Map<string, string>();
    allDevices.forEach((d) => {
      map.set(d.id, d.identifier);
    });
    return map;
  }, [allDevices]);

  const handlePrepareUnassign = async (pt: AUTProjectTank) => {
    try {
      const stepTanks = await apiService.irrigationPlanStepTank.query({
        filter: { project_tank: { _eq: pt.id } },
        fields: ["id"],
      } as any);
      setStepTankCount(stepTanks.length);
    } catch {
      setStepTankCount(0);
    }
    setUnassignTarget(pt);
  };

  const handleUnassign = async () => {
    if (!unassignTarget) return;
    setUnassigning(true);
    try {
      const now = new Date().toISOString();
      await updateProjectTank({
        id: unassignTarget.id,
        data: { end_date: now } as any,
      });
      await refetchData();
    } finally {
      setUnassigning(false);
      setUnassignTarget(null);
      setStepTankCount(0);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {activeProjectTanks.length > 0 ? (
        activeProjectTanks.map((pt) => {
          const activeVdc = pt.vdc_configurations.find((v) => !v.end_date);
          return (
            <div
              key={pt.id}
              className="bg-white rounded-xl border border-neutral-100 p-6 hover:shadow-md transition-all duration-150 ease-in-out"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-neutral-900 mb-2 text-base flex items-center gap-2">
                    <Cylinder className="w-4 h-4 text-blue-500" />
                    {pt.tank.label}
                  </h3>
                  <div className="text-sm text-neutral-600 space-y-1">
                    <div>Nutriente: {pt.tank.nutrient.name}</div>
                    <div>Vazão: {pt.flow_rate_lh} L/h</div>
                    {activeVdc && (
                      <div>
                        Controlador: {deviceById.get(activeVdc.device) || "N/A"} — Saída:{" "}
                        {activeVdc.turn_on_physical_output_index}
                      </div>
                    )}
                    <div>Início: {new Date(pt.start_date).toLocaleDateString("pt-BR")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditTarget(pt)}
                    className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar controlador"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handlePrepareUnassign(pt)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title="Desatribuir tanque"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center py-12">
          <div className="text-neutral-400 mb-4">
            <Cylinder className="mx-auto h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">Nenhum tanque atribuído</h3>
          <p className="text-neutral-600 mb-6 max-w-sm mx-auto">
            Atribua tanques de fertilizantes a este projeto para configurar a fertirrigação.
          </p>
          <Button onClick={onAssign} icon={<Plus className="h-4 w-4" />} iconPosition="left">
            Atribuir Tanque
          </Button>
        </div>
      )}

      {activeProjectTanks.length > 0 && <FAB className="bottom-20 right-4" onClick={onAssign} />}

      <ConfirmModal
        isOpen={unassignTarget !== null}
        onClose={() => !unassigning && setUnassignTarget(null)}
        onConfirm={handleUnassign}
        title="Desatribuir tanque"
        message={`Remover o tanque "${unassignTarget?.tank.label}" deste projeto? A configuração do controlador também será removida.${stepTankCount > 0 ? `\n\nEste tanque é usado em ${stepTankCount} etapa${stepTankCount === 1 ? "" : "s"} de irrigação. As receitas serão removidas.` : ""}`}
        confirmText={unassigning ? "Removendo..." : "Remover"}
        cancelText="Cancelar"
        variant="danger"
        confirmEnabled={!unassigning}
      />

      <EditTankControllerDialog
        key={editTarget?.id ?? "none"}
        projectTank={editTarget}
        isOpen={editTarget !== null}
        onClose={() => setEditTarget(null)}
      />
    </div>
  );
}

export default ProjectTanksPanel;
